<template>
  <v-card flat>
    <v-card-title>
      Hidden Ingredient Words
      <v-spacer/>
      <v-btn text @click="reload" :loading="loading">Reload</v-btn>
      <v-btn color="primary" class="ml-2" @click="save" :loading="saving" :disabled="!dirty">Save</v-btn>
    </v-card-title>
    <v-card-subtitle>
      When a customer loves or hates a suggested box meal, the app offers the meal's ingredients as
      one-tap chips — compound names whole and word by word ("chicken breast" → "Chicken", "Breast").
      Words listed here are never offered as chips (customers can still type them). Lowercase, one per
      line; paste a list to replace it.
    </v-card-subtitle>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000">{{ snackbarText }}</v-snackbar>

    <v-card-text>
      <v-row>
        <!-- the list -->
        <v-col cols="12" md="4">
          <v-textarea
            v-model="text"
            label="Hidden words (one per line)"
            outlined
            auto-grow
            rows="18"
            :hint="`${words.length} words${dirty ? ' — unsaved changes' : ''}`"
            persistent-hint
          />
          <div class="mt-3">
            <v-chip
              v-for="w in words"
              :key="w"
              small
              class="ma-1"
              close
              @click:close="removeWord(w)"
            >{{ w }}</v-chip>
          </div>
        </v-col>

        <!-- the week's words, to check the list against real menu data -->
        <v-col cols="12" md="8">
          <v-row no-gutters align="center" class="mb-2">
            <v-text-field
              v-model="date"
              label="Order week (Sunday)"
              hint="Blank = the next ordering week"
              persistent-hint
              dense
              outlined
              class="mr-3"
              style="max-width: 220px"
              @keyup.enter="loadMenuWords"
            />
            <v-btn text @click="loadMenuWords" :loading="loadingMenu">Load box catalog words</v-btn>
            <v-spacer/>
            <v-text-field
              v-model="filter"
              label="Filter"
              dense
              outlined
              clearable
              style="max-width: 220px"
            />
          </v-row>
          <div v-if="menu" class="text-caption mb-2">
            Week of {{ menu.orderDate }} · {{ menu.meals }} meals · {{ menu.words.length }} words from
            {{ menu.phrases.length }} compound / simple ingredient names.
            Click a word to hide or unhide it (then Save).
            <b>{{ offeredWords.length }} offered</b>, {{ hiddenWords.length }} hidden.
          </div>
          <div v-else class="text-caption mb-2">Load a week to see every word the app would offer.</div>

          <template v-if="menu">
            <div class="text-subtitle-2 mt-2">Offered as chips</div>
            <div>
              <v-chip
                v-for="w in offeredWords"
                :key="`o-${w.word}`"
                small
                color="green lighten-4"
                class="ma-1"
                :title="w.phrases.join(', ')"
                @click="hideWord(w.word)"
              >
                {{ w.word }} <span class="grey--text ml-1">{{ w.count }}</span>
              </v-chip>
              <span v-if="!offeredWords.length" class="text-caption grey--text">none</span>
            </div>

            <div class="text-subtitle-2 mt-4">Hidden</div>
            <div>
              <v-chip
                v-for="w in hiddenWords"
                :key="`h-${w.word}`"
                small
                class="ma-1 text-decoration-line-through"
                :title="w.phrases.join(', ')"
                @click="removeWord(w.word)"
              >
                {{ w.word }} <span class="grey--text ml-1">{{ w.count }}</span>
              </v-chip>
              <span v-if="!hiddenWords.length" class="text-caption grey--text">none</span>
            </div>

            <div class="text-subtitle-2 mt-4">Ingredient names on the menu</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Ingredient</th>
                  <th>Meals</th>
                  <th>Offered as chips</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in filteredPhrases" :key="p.phrase">
                  <td>{{ p.phrase }}</td>
                  <td>{{ p.count }}</td>
                  <td>
                    <span v-for="w in chipsFor(p)" :key="`${p.phrase}-${w}`" class="mr-2">{{ w }}</span>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import api from "@/api";

// the word splitting itself (3+ letters, joiners skipped, only compound
// names split) is the server's — fed-nutrition-api
// ingredient-stop-words.ts, mirrored by the customer app's
// FedIngredientPromptDialog; this page only edits the hidden list
// one word per line (commas / semicolons work too); "#" lines are comments
// so the seed file (docs/ingredient-stop-words.txt) pastes in as is
function parseWords(text) {
  const out = new Set();
  const lines = Array.isArray(text) ? text : String(text || '').split(/\n+/);
  for (const line of lines) {
    if (String(line).trim().startsWith('#')) continue;
    for (const w of String(line).split(/[,;]+/)) {
      const word = w.trim().toLowerCase().replace(/\s+/g, ' ');
      if (word) out.add(word);
    }
  }
  return [...out].sort();
}

export default {
  name: "IngredientStopWords",
  data() {
    return {
      text: '',
      saved: [],
      loading: false,
      saving: false,
      menu: null,
      loadingMenu: false,
      date: '',
      filter: '',
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
    };
  },
  computed: {
    words() {
      return parseWords(this.text);
    },
    wordSet() {
      return new Set(this.words);
    },
    dirty() {
      return this.words.join('\n') !== this.saved.join('\n');
    },
    menuWords() {
      if (!this.menu) return [];
      const f = (this.filter || '').trim().toLowerCase();
      return this.menu.words.filter(w => !f || w.word.includes(f) || w.phrases.some(p => p.includes(f)));
    },
    offeredWords() {
      return this.menuWords.filter(w => !this.wordSet.has(w.word));
    },
    hiddenWords() {
      return this.menuWords.filter(w => this.wordSet.has(w.word));
    },
    filteredPhrases() {
      if (!this.menu) return [];
      const f = (this.filter || '').trim().toLowerCase();
      return this.menu.phrases.filter(p => !f || p.phrase.includes(f));
    },
  },
  methods: {
    notify(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    // what the app offers for one ingredient name: the name itself (unless
    // hidden) and, for a compound, each of its words not hidden
    chipsFor(p) {
      const out = [];
      if (!this.wordSet.has(p.phrase)) out.push(p.phrase);
      for (const w of p.words) {
        if (!this.wordSet.has(w) && !out.includes(w)) out.push(w);
      }
      return out;
    },
    hideWord(word) {
      if (this.wordSet.has(word)) return;
      this.text = [...this.words, word].sort().join('\n');
    },
    removeWord(word) {
      this.text = this.words.filter(w => w !== word).join('\n');
    },
    async reload() {
      this.loading = true;
      try {
        const {data} = await api.get('v2/ingredient_stop_words');
        this.saved = parseWords(data.words || []);
        this.text = this.saved.join('\n');
      } catch (e) {
        console.error('failed to load ingredient stop words', e);
        this.notify('Could not load the hidden words', 'error');
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      try {
        const {data} = await api.put('v2/ingredient_stop_words', {words: this.words});
        this.saved = parseWords(data.words || []);
        this.text = this.saved.join('\n');
        this.notify(`Saved ${this.saved.length} hidden words`);
      } catch (e) {
        console.error('failed to save ingredient stop words', e);
        this.notify('Could not save the hidden words', 'error');
      } finally {
        this.saving = false;
      }
    },
    async loadMenuWords() {
      this.loadingMenu = true;
      try {
        const params = this.date ? {date: this.date} : {};
        const {data} = await api.get('v2/ingredient_stop_words/menu-words', {params});
        this.menu = data;
      } catch (e) {
        console.error('failed to load menu words', e);
        this.notify('Could not load the box catalog words for that week', 'error');
      } finally {
        this.loadingMenu = false;
      }
    },
  },
  mounted() {
    this.reload();
    this.loadMenuWords();
  },
};
</script>
